// Editor-page rules that keep a field's allowed-value map in step with the
// document (feature 004). Pure and environment-agnostic so they can be unit
// tested without mounting the editor.

import { reconcileFieldOptions, type FieldOptions } from '@template-filler/core';

/**
 * Move a field's option set to its new name when it is renamed (FR-011).
 *
 * The renamed field's set wins: if the new name already had a set, it is
 * replaced (source wins), matching the way renaming merges two fields into the
 * one being renamed. If the renamed field has no set, the destination keeps its
 * own — there is nothing to move.
 */
export function renameFieldOptions(
  options: FieldOptions,
  oldName: string,
  newName: string,
): FieldOptions {
  if (oldName === newName) return options;
  const source = options[oldName];
  if (source === undefined) return options;

  const next: FieldOptions = { ...options };
  delete next[oldName];
  next[newName] = source;
  return next;
}

/**
 * The option map to send on save: only fields the document still contains
 * (orphans dropped, mirroring the server's reconciliation, D-03) and no empty
 * sets (an empty set means the field is free text). May be empty, which the
 * server treats as "no constrained fields".
 */
export function fieldOptionsForSave(
  options: FieldOptions,
  names: readonly string[],
): FieldOptions {
  const reconciled = reconcileFieldOptions(options, names);
  const out: FieldOptions = {};
  for (const [name, values] of Object.entries(reconciled)) {
    if (values.length > 0) out[name] = values;
  }
  return out;
}
